// Site-level Log in modal. Opened from the header "Log in" button (Events.OpenLogin).
// A clean email/password form + Google + forgot-password + a link over to the
// Register (adopt-a-Mao) flow. Lives at the body level so it works on any page.

import { Events, on, emit } from './eventBus.js'
import { login, loginWithGoogle, requestPasswordReset } from '../game/accountManager.js'
import { setActivePage } from './siteRouter.js'

// Ask Supabase which sign-in providers are enabled. If we can't tell (offline, odd
// response), keep the button — the click handler reports any failure.
async function googleEnabled() {
  const env = import.meta.env ?? {}
  const url = env.VITE_SUPABASE_URL
  const key = env.VITE_SUPABASE_ANON_KEY
  if (!url || !key) return false
  try {
    const r = await fetch(`${url}/auth/v1/settings`, { headers: { apikey: key } })
    if (!r.ok) return true
    const settings = await r.json()
    return settings?.external?.google !== false
  } catch {
    return true
  }
}

export function mountLoginModal() {
  if (document.querySelector('.login-modal')) return // idempotent

  const wrap = document.createElement('div')
  wrap.className = 'login-modal'
  wrap.innerHTML = `
    <div class="login-modal__backdrop" data-login-close></div>
    <div class="login-modal__card" role="dialog" aria-modal="true" aria-label="Log in">
      <button class="login-modal__x" data-login-close aria-label="Close">×</button>
      <h2 class="login-modal__title">Welcome back</h2>
      <p class="login-modal__sub">Log in to your kingdom.</p>
      <form data-login-form>
        <input class="login-modal__input" type="email" name="email" placeholder="Email" autocomplete="email" required>
        <input class="login-modal__input" type="password" name="password" placeholder="Password" autocomplete="current-password" required>
        <div class="login-modal__msg" data-login-msg></div>
        <button class="login-modal__submit" type="submit" data-login-submit>Log in</button>
      </form>
      <button class="login-modal__google" data-login-google>
        <svg class="login-modal__google-g" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        <span>Continue with Google</span>
      </button>
      <div class="login-modal__links">
        <button type="button" class="login-modal__link" data-login-forgot>Forgot password?</button>
        <span>New here? <button type="button" class="login-modal__link" data-login-register>Register</button></span>
      </div>
    </div>
  `
  document.body.appendChild(wrap)

  const form = wrap.querySelector('[data-login-form]')
  const msg = wrap.querySelector('[data-login-msg]')
  const submit = wrap.querySelector('[data-login-submit]')
  const setMsg = (t, ok = false) => { msg.textContent = t; msg.classList.toggle('ok', ok) }

  const open = () => { setMsg(''); wrap.classList.add('open'); form.querySelector('[name=email]').focus() }
  const close = () => wrap.classList.remove('open')

  wrap.querySelectorAll('[data-login-close]').forEach((b) => b.addEventListener('click', close))
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && wrap.classList.contains('open')) close() })

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    const email = form.email.value.trim()
    const password = form.password.value
    submit.disabled = true
    setMsg('Logging in…')
    const res = await login(email, password)
    submit.disabled = false
    if (res.ok) { close(); setActivePage('play') }
    else setMsg(res.reason || 'Login failed.')
  })

  // Hide "Continue with Google" until the Google provider is actually switched on in
  // Supabase — otherwise the click lands on a raw JSON error page.
  const googleBtn = wrap.querySelector('[data-login-google]')
  googleBtn.hidden = true
  googleEnabled().then((enabled) => { googleBtn.hidden = !enabled })

  googleBtn.addEventListener('click', async () => {
    setMsg('Redirecting to Google…')
    const res = await loginWithGoogle()
    if (!res.ok) setMsg(res.reason || 'Google sign-in failed.')
  })

  // Forgot password needs a working email sender (see docs/SETUP-CHECKLIST.md, step 5).
  // Flip this to true once Supabase SMTP is configured and the link comes back.
  const SHOW_FORGOT_PASSWORD = false
  wrap.querySelector('[data-login-forgot]').hidden = !SHOW_FORGOT_PASSWORD

  wrap.querySelector('[data-login-forgot]').addEventListener('click', async () => {
    const email = form.email.value.trim()
    if (!email) { setMsg('Enter your email above first, then tap “Forgot password.”'); return }
    const res = await requestPasswordReset(email)
    setMsg(res.ok ? 'Password reset email sent — check your inbox.' : (res.reason || 'Could not send reset email.'), res.ok)
  })

  wrap.querySelector('[data-login-register]').addEventListener('click', () => {
    close()
    setActivePage('play')
    emit(Events.StartAdoption)
  })

  on(Events.OpenLogin, open)
}
